import { useCallback, useEffect, useState } from 'react';
import { api, setzeAbmeldeHandler, type Nutzer } from './api/client';
import { ApiStatus } from './components/ApiStatus';
import { link, useRoute } from './router';
import { UndoKnoepfe, UndoProvider, useUndo } from './undo';
import { Anmeldung } from './screens/Anmeldung';
import { Ausgabe } from './screens/Ausgabe';
import { Druck } from './screens/Druck';
import { Eckdaten } from './screens/Eckdaten';
import { Einstellungen } from './screens/Einstellungen';
import { Kraefte } from './screens/Kraefte';
import { Planung } from './screens/Planung';
import { Postenkarte } from './screens/Postenkarte';
import { Uebersicht } from './screens/Uebersicht';
import { Vorgaben } from './screens/Vorgaben';

export function App() {
  return (
    <UndoProvider>
      <AppInhalt />
    </UndoProvider>
  );
}

function AppInhalt() {
  const { leeren } = useUndo();
  const pfad = useRoute();
  const [nutzer, setNutzer] = useState<Nutzer | null | undefined>(undefined);

  const pruefen = useCallback(() => {
    api
      .ich()
      .then((r) => setNutzer(r.nutzer))
      .catch(() => setNutzer(null));
  }, []);

  useEffect(() => {
    setzeAbmeldeHandler(() => {
      setNutzer(null);
      leeren();
    });
    pruefen();
    return () => setzeAbmeldeHandler(null);
  }, [pruefen, leeren]);

  // Ohne Anmeldung erreichbar: geteilte Pläne und Druckansichten daraus, Postenkarte (offline aus Cache)
  if (pfad[0] === 'geteilt' && pfad[1]) {
    return <Druck quelle={{ token: pfad[1] }} art={pfad[2] ?? 'uebersicht'} />;
  }
  if (pfad[0] === 'druck' && pfad[1]) {
    return <Druck quelle={{ planversion: pfad[1] }} art={pfad[2] ?? 'uebersicht'} />;
  }
  if (pfad[0] === 'postenkarte' && pfad[1] && pfad[2]) {
    return <Postenkarte planversion={pfad[1]} postenId={pfad[2]} />;
  }

  return (
    <div className="app">
      <header className="kopfleiste">
        <a className="kopfleiste__marke" href="#/">
          <img src="./icon.svg" alt="" />
          Umzugsplaner
        </a>
        {nutzer && (
          <nav className="kopfleiste__nav" aria-label="Hauptmenü">
            <a href="#/" aria-current={pfad.length === 0 ? 'page' : undefined}>
              Übersicht
            </a>
            <a
              href={link('einstellungen')}
              aria-current={pfad[0] === 'einstellungen' ? 'page' : undefined}
            >
              Einstellungen
            </a>
          </nav>
        )}
        <div className="kopfleiste__rechts">
          {nutzer?.rolle === 'planer' && <UndoKnoepfe />}
          <ApiStatus />
          {nutzer && (
            <span className="kopfleiste__nutzer">
              {nutzer.name} ({nutzer.rolle === 'planer' ? 'Planer' : 'Leser'})
              <button
                type="button"
                className="knopf knopf--hell"
                onClick={() =>
                  void api.abmelden().finally(() => {
                    setNutzer(null);
                    leeren();
                  })
                }
              >
                Abmelden
              </button>
            </span>
          )}
        </div>
      </header>
      <main className="hauptbereich">
        {nutzer === undefined ? (
          <p className="laedt">Lädt …</p>
        ) : nutzer === null ? (
          <Anmeldung onAngemeldet={setNutzer} />
        ) : (
          <Inhalt pfad={pfad} nutzer={nutzer} />
        )}
      </main>
      <footer className="fusszeile">
        Planungshilfe – die Verantwortung liegt bei der Einsatzleitung. Alle Zeit- und
        Besetzungswerte sind anpassbare Annahmen, keine Norm. Kartendaten ©
        OpenStreetMap-Mitwirkende.
      </footer>
    </div>
  );
}

function Inhalt({ pfad, nutzer }: { pfad: string[]; nutzer: Nutzer }) {
  const darf = nutzer.rolle === 'planer';
  if (pfad[0] === 'einstellungen') return <Einstellungen nutzer={nutzer} />;
  if (pfad[0] === 'neu') return <Eckdaten id={null} darf={darf} />;
  if (pfad[0] === 'v' && pfad[1]) {
    const id = pfad[1];
    switch (pfad[2]) {
      case 'eckdaten':
        return <Eckdaten id={id} darf={darf} />;
      case 'vorgaben':
        return <Vorgaben id={id} nutzer={nutzer} />;
      case 'kraefte':
        return <Kraefte id={id} darf={darf} />;
      case 'ausgabe':
        return <Ausgabe id={id} darf={darf} />;
      default:
        return <Planung id={id} darf={darf} />;
    }
  }
  return <Uebersicht darf={darf} />;
}
